import { useState, useEffect, useCallback } from 'react';
import { firestore, auth } from '../firebase';
import {
  collection, addDoc, updateDoc, query, where,
  getDocs, onSnapshot, doc, getDoc
} from 'firebase/firestore';

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const isThisMonth = (date) => {
  const now = new Date();
  const d = date?.toDate ? date.toDate() : new Date(date);
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
};

const computeStats = (challenges) => {
  const monthlyAttempts = challenges.filter(c => isThisMonth(c.completedAt)).length;
  return {
    totalCompleted:  challenges.length,
    totalSolved:     challenges.filter(c => c.status === 'solved').length,
    averageScore:    challenges.length > 0
      ? Math.round(challenges.reduce((sum, c) => sum + c.score, 0) / challenges.length)
      : 0,
    bestScore:       challenges.length > 0 ? Math.max(...challenges.map(c => c.score)) : 0,
    monthlyAttempts,
  };
};

const stripUndefined = (obj) =>
  Object.entries(obj).reduce((acc, [k, v]) => {
    if (v !== undefined) acc[k] = v;
    return acc;
  }, {});

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useChallengeStorage = () => {
  const [completedChallenges, setCompletedChallenges] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [userPlan,  setUserPlan]  = useState('free');
  const [stats,     setStats]     = useState({
    totalCompleted:  0,
    totalSolved:     0,
    averageScore:    0,
    bestScore:       0,
    monthlyAttempts: 0,
  });

  const PLANS = {
    free:    { name: 'Free',    challenges: 3    },
    starter: { name: 'Starter', challenges: 200  },
    pro:     { name: 'Pro',     challenges: 500  },
    premium: { name: 'Premium', challenges: 1400 },
  };

  useEffect(() => {
    const userId = auth.currentUser?.uid;
    if (!userId) { setLoading(false); return; }

    // Fetch plan once
    const fetchUserPlan = async () => {
      try {
        const snap = await getDoc(doc(firestore, 'users', userId));
        setUserPlan(snap.exists() ? (snap.data().plan || 'free') : 'free');
      } catch {
        setUserPlan('free');
      }
    };
    fetchUserPlan();

    // Real-time listener
    try {
      const q = query(
        collection(firestore, 'completedChallenges'),
        where('userId', '==', userId)
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const challenges = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          setCompletedChallenges(challenges);
          setStats(computeStats(challenges));
          setLoading(false);
        },
        (error) => {
          console.error('Snapshot error:', error);
          if (error.code === 'failed-precondition' || error.message.includes('index')) {
            fallbackFetch(userId);
          } else {
            setLoading(false);
          }
        }
      );

      return () => unsubscribe();
    } catch (error) {
      console.error('Listener setup error:', error);
      setLoading(false);
    }
  }, []);

  const fallbackFetch = async (userId) => {
    try {
      const q = query(
        collection(firestore, 'completedChallenges'),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(q);
      const challenges = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.completedAt?.seconds || 0) - (a.completedAt?.seconds || 0));

      setCompletedChallenges(challenges);
      setStats(computeStats(challenges));
    } catch (error) {
      console.error('Fallback fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const canAttemptChallenge = useCallback(() => {
    const userId = auth.currentUser?.uid;
    if (!userId) return { allowed: false, reason: 'Please log in to attempt challenges.' };

    const currentPlan = PLANS[userPlan];
    if (!currentPlan) return { allowed: false, reason: 'Invalid plan. Please contact support.' };

    if (stats.monthlyAttempts >= currentPlan.challenges) {
      return {
        allowed: false,
        reason: `Monthly limit reached (${currentPlan.challenges} challenges). Upgrade for more!`,
      };
    }

    return { allowed: true };
  }, [userPlan, stats.monthlyAttempts]);

  const getUsageDisplay = useCallback(() => {
    const currentPlan = PLANS[userPlan];
    if (!currentPlan) return null;
    return {
      monthlyUsed:      stats.monthlyAttempts,
      monthlyLimit:     currentPlan.challenges,
      monthlyRemaining: currentPlan.challenges - stats.monthlyAttempts,
      totalCompleted:   stats.totalCompleted,
      plan:             currentPlan.name,
    };
  }, [userPlan, stats]);

  const saveChallenge = useCallback(async (challengeData) => {
    const userId    = auth.currentUser?.uid;
    const userEmail = auth.currentUser?.email;
    if (!userId) return { error: 'User not authenticated' };

    // Single access check here — callers do not need to re-check
    const accessCheck = canAttemptChallenge();
    if (!accessCheck.allowed) return { error: accessCheck.reason };

    try {
      const aiResponse = challengeData.aiResponse || challengeData.idealPrompt;
      const feedback   = challengeData.feedback   || challengeData.message;

      const existingSnap = await getDocs(query(
        collection(firestore, 'completedChallenges'),
        where('userId',   '==', userId),
        where('language', '==', challengeData.language),
        where('title',    '==', challengeData.title)
      ));

      if (!existingSnap.empty) {
        const existingDoc  = existingSnap.docs[0];
        const existingData = existingDoc.data();

        const updated = stripUndefined({
          bestScore:    Math.max(existingData.bestScore || 0, challengeData.score),
          score:        challengeData.score,
          attempts:     (existingData.attempts || 1) + 1,
          status:       challengeData.status === 'solved' ? 'solved' : existingData.status,
          finalPrompt:  challengeData.finalPrompt,
          aiResponse,
          feedback,
          breakdown:    challengeData.breakdown,
          details:      challengeData.details,
          completedAt:  new Date(),
        });

        await updateDoc(doc(firestore, 'completedChallenges', existingDoc.id), updated);
        return existingDoc.id;

      } else {
        const newDoc = stripUndefined({
          userId,
          userEmail,
          language:    challengeData.language,
          title:       challengeData.title,
          description: challengeData.description,
          topic:       challengeData.topic,
          score:       challengeData.score,
          bestScore:   challengeData.score,
          attempts:    1,
          difficulty:  challengeData.difficulty,
          status:      challengeData.status,
          objective:   challengeData.objective,
          constraints: challengeData.constraints,
          finalPrompt: challengeData.finalPrompt,
          aiResponse,
          feedback,
          breakdown:   challengeData.breakdown,
          details:     challengeData.details,
          completedAt: new Date(),
          timeSpent:   challengeData.timeSpent,
        });

        const docRef = await addDoc(collection(firestore, 'completedChallenges'), newDoc);
        return docRef.id;
      }

    } catch (error) {
      console.error('Error saving challenge:', error);
      return { error: error.message, code: error.code, details: error.toString() };
    }
  }, [canAttemptChallenge]);

  const isChallengeCompleted = useCallback((language, title) =>
    completedChallenges.some(c => c.language === language && c.title === title && c.status === 'solved'),
    [completedChallenges]
  );

  const getChallengeDetails = useCallback((language, title) =>
    completedChallenges.find(c => c.language === language && c.title === title),
    [completedChallenges]
  );

  return {
    completedChallenges,
    loading,
    stats,
    userPlan,
    PLANS,
    saveChallenge,
    isChallengeCompleted,
    getChallengeDetails,
    canAttemptChallenge,
    getUsageDisplay,
  };
};